import { api, type ApiResponse } from '../client';

/**
 * Interface for recent pending requests in the dashboard
 */
export interface RecentPendingRequest {
  message: string;
  date: string;
  type: string;
  status: string;
  uuid: string;
  approved_at: string;
  total_donation: number;
  leave_hours: number;
  charity: {
    name: string;
  };
  user: {
    full_name: string;
    job_title: string;
  };
  created_at: string;
}

export interface RecentPendingPayment {
  uuid: string;
  employee_name: string;
  charity_name: string;
  hours: number;
  amount: number;
  approved_at: string | null;
  lifecycle_stage: string;
}

export interface RecentCompletedPayment {
  uuid: string;
  invoice_id: string;
  charity_name: string;
  amount: number;
  payment_date: string | null;
  reference_number: string | null;
  lifecycle_stage: string;
}

/**
 * Interface for employer dashboard data
 */
export interface EmployerDashboardData {
  pending_requests_count: number;
  pending_payments_count: number;
  pending_payments_value: number;
  completed_payments_count: number;
  completed_payments_value: number;
  recent_pending_requests: RecentPendingRequest[];
  recent_pending_payments: RecentPendingPayment[];
  recent_completed_payments: RecentCompletedPayment[];
}

/**
 * Interface for employer dashboard response
 */
export interface EmployerDashboardResponse {
  success: boolean;
  data: EmployerDashboardData;
  errors: string[];
}

/**
 * Interface for dashboard query parameters
 */
export interface DashboardParams {
  page?: number;
  per_page?: number;
}

// API base URL for employer dashboard
const API_BASE_URL = '/employer-dashboard';

export class EmployerDashboardService {
  private static instance: EmployerDashboardService;

  private constructor() {}

  public static getInstance(): EmployerDashboardService {
    if (!EmployerDashboardService.instance) {
      EmployerDashboardService.instance = new EmployerDashboardService();
    }
    return EmployerDashboardService.instance;
  }

  /**
   * Get dashboard data for the authenticated employer
   * @param params Optional pagination parameters
   * @returns Promise with the dashboard data
   */
  public async getDashboard(params?: DashboardParams): Promise<EmployerDashboardResponse> {
    try {
      const response = await api.get<EmployerDashboardResponse>(API_BASE_URL, {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employer dashboard:', error);
      throw error;
    }
  }
}

export default EmployerDashboardService.getInstance();
